import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { UserAvatar, Attachment, HtmlRender, NameLabel } from 'components/elements';
import { useDeviceDetect } from 'hooks';
import { dateUtils } from 'utils';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function ChatMessage({ body, attachments, createdAt, id, senderId, showAvatar, showSender, showTimestamp, isUser, isVideoChat, participants, onDownload, downloadingList }) {
  const { t } = useTranslation('mailboxer');
  const classes = useStyles();
  const { isDesktop } = useDeviceDetect();
  const dateTimeCreated = dateUtils.formatTimeString(createdAt);
  const hasMessageBody =
    body !== null &&
    body !== `(${t('errors.no_body', { lng: 'en' })})` &&
    body !== `(${t('errors.no_body', { lng: 'fr' })})` &&
    body !== `(${t('errors.no_body', { lng: 'es' })})` &&
    body !== '<p><br></p>' &&
    body !== '<p></p>';

  const messageSender = participants.filter(({ id: participantId }) => participantId === senderId)[0];
  const { name: participantName, email: participantEmail, fullName, personalPronoun } = messageSender || {};

  const messagesWrapperClasses = cn(
    classes.messagesWrapper,
    { [classes.userMessagesWrapper]: isUser },
    { [classes.userMessageWithoutAvatar]: !showAvatar },
    { [classes.userMessageInfoSenderWrapper]: showSender },
  );
  const dateClasses = cn(classes.date, { [classes.userDate]: isUser });
  const messageInfoWrapperClasses = cn(classes.messageInfoWrapper, { [classes.userMessageInfoWrapper]: isUser });
  const messageClasses = cn(classes.message, { [classes.userMessage]: isUser });

  return hasMessageBody || attachments.length ? (
    <div className={messagesWrapperClasses} key={id}>
      {!isUser && <div className={!isVideoChat ? classes.avatar : classes.videoChatAvatar}>{showAvatar && <UserAvatar />}</div>}
      <div className={messageInfoWrapperClasses}>
        {!isUser && isDesktop && showSender && (
          <Typography className={classes.sender} variant="caption" component="span">
            <NameLabel fullName={fullName || participantName || participantEmail} personalPronoun={personalPronoun} options={{ pronoun: { parentheses: true, mutedText: true } }} />
          </Typography>
        )}
        {hasMessageBody && (
          <div className={messageClasses}>
            <HtmlRender data={body} htmlProfile />
          </div>
        )}
        {attachments?.map(({ id: attachmentId, name, isExpired, isDeleted, key }) => (
          <Attachment
            classes={classes.messageAttachment}
            onClick={() => onDownload(id, attachmentId, name, key)}
            key={attachmentId}
            text={name}
            isDisabled={isExpired || isDeleted || downloadingList.includes(attachmentId)}
          />
        ))}
        {showTimestamp && <div className={dateClasses}>{dateTimeCreated}</div>}
      </div>
    </div>
  ) : null;
}

ChatMessage.propTypes = {
  body: PropTypes.string.isRequired,
  attachments: PropTypes.arrayOf(PropTypes.object).isRequired,
  createdAt: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  senderId: PropTypes.string,
  showAvatar: PropTypes.bool.isRequired,
  showSender: PropTypes.bool.isRequired,
  showTimestamp: PropTypes.bool.isRequired,
  isUser: PropTypes.bool.isRequired,
  isVideoChat: PropTypes.bool,
  participants: PropTypes.arrayOf(PropTypes.object),
  onDownload: PropTypes.func.isRequired,
  downloadingList: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ChatMessage.defaultProps = {
  isVideoChat: false,
  participants: [],
  senderId: '',
};

export default ChatMessage;
